import { CancelToken } from "axios";

import { SimpleResult } from "../dto";
import { ApiSuffixType } from "../enums";
import { Api, apiUrlBase, Log } from "../helpers";
import { ISearchCriteria, RequestBodyModel, RydeUiUnit } from "../models";

export class CrudApi {
  static async getAll<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    suffixType: ApiSuffixType = ApiSuffixType.DropdownItems,
    customSuffix = "",
  ): Promise<T[] | undefined> {
    const urlBase = apiUrlBase(entity, suffixType, customSuffix);
    Log.debug(
      `Crud api mapping for ${entity}, api url: ${urlBase}, suffix type: ${ApiSuffixType[suffixType]}`,
    );
    const response = await Api.get<T[]>(urlBase);

    return response?.data;
  }

  static async getOne<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    suffixType: ApiSuffixType = ApiSuffixType.NoSuffix,
  ): Promise<T | undefined> {
    const urlBase = apiUrlBase(entity, suffixType);
    const response = await Api.get<T>(urlBase);
    return response?.data;
  }

  static async getById<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    id: string | number,
  ): Promise<T | undefined> {
    const urlBase = apiUrlBase(entity);
    const response = await Api.get<T>(`${urlBase}/${id}`);
    return response?.data;
  }

  static async create<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    data: Partial<T>,
  ): Promise<T | undefined> {
    const urlBase = apiUrlBase(entity);
    const response = await Api.post<T>(urlBase, { ...data });

    return response?.data;
  }

  static async getOrCreate<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    data: Partial<T>,
  ): Promise<SimpleResult<T> | undefined> {
    const urlBase = apiUrlBase(entity);
    const response = await Api.post<SimpleResult<T>>(`${urlBase}/getOrCreate`, {
      ...data,
    });

    return response?.data;
  }

  static async update<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    id: number,
    data: Partial<T>,
  ): Promise<T | undefined> {
    const urlBase = apiUrlBase(entity);
    const response = await Api.patch<T>(`${urlBase}/${id}`, { ...data });

    return response?.data;
  }

  static async post<T extends RequestBodyModel>({
    entity,
    suffixType = ApiSuffixType.NoSuffix,
    data,
    abortController,
    cancelToken,
  }: {
    entity: RydeUiUnit;
    suffixType: ApiSuffixType;
    data: ISearchCriteria<unknown>;
    abortController?: AbortController;
    cancelToken?: CancelToken;
  }): Promise<T | undefined> {
    const urlBase = apiUrlBase(entity, suffixType);
    Log.debug(
      `Crud POST api mapping for ${entity}, api url: ${urlBase}, suffix type: ${ApiSuffixType[suffixType]}`,
    );
    const response = await Api.postExtended<T>({
      url: urlBase,
      body: { ...data },
      abortController,
      cancelToken,
    });

    return response?.data;
  }

  static async delete<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    id: number,
  ): Promise<T | undefined> {
    const urlBase = apiUrlBase(entity);
    const response = await Api.delete<T>(`${urlBase}/${id}`);

    return response?.data;
  }

  static async deleteWithParams<T extends RequestBodyModel>(
    entity: RydeUiUnit,
    id: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: any,
  ): Promise<T | undefined> {
    const urlBase = apiUrlBase(entity);
    const response = await Api.delete<T>(`${urlBase}/${id}`, payload);

    return response?.data;
  }
}
